"use client";

// Documentation content — French. Mirrors public/documentation-fr.html.
// Édition en langage simple, remaniée pour couvrir les niveaux de propriété
// et les outils de gestion sur les pages.

import {
  Callout,
  DocBadge,
  DocTable,
  L,
  OL,
  P,
  type DocSection,
} from "./primitives";

export const sectionsFR: DocSection[] = [
  // ---- Partie 1 · Commencez ici ----
  {
    id: "what-is-homebook",
    num: "1.1",
    part: 1,
    title: "C'est quoi, HomeBook ?",
    body: (
      <>
        <P>
          HomeBook, c'est un calendrier familial partagé pour nos maisons. Chaque
          maison a sa page avec photos, prix et calendrier. Vous choisissez vos
          jours, et les responsables disent oui ou non. C'est toute l'idée.
        </P>
        <L>
          <li>
            Ça marche sur n'importe quel téléphone, tablette ou ordinateur — rien
            à installer.
          </li>
          <li>
            Chaque personne se connecte avec son propre compte, comme ça le
            calendrier montre toujours qui dort où.
          </li>
        </L>
      </>
    ),
  },
  {
    id: "test-accounts",
    num: "1.2",
    part: 1,
    title: "Comptes de test",
    body: (
      <>
        <P>
          Le site arrive avec des comptes tout prêts pour essayer chaque rôle.
          Connectez-vous avec le <strong>nom d'utilisateur</strong> (pas
          l'e-mail) :
        </P>
        <DocTable
          head={["Nom d'utilisateur", "Mot de passe", "À quoi sert ce compte"]}
          rows={[
            [
              <code key="a">admin</code>,
              <code key="ap">{"admin" + "123"}</code>,
              "Le chef. Voit tout et peut tout faire : utilisateurs, maisons, argent, réglages.",
            ],
            [
              <code key="m">mod</code>,
              <code key="mp">{"mod" + "123"}</code>,
              "Un aide. Peut être assigné à des maisons pour valider les réservations et gérer les factures.",
            ],
            [
              <code key="f">family</code>,
              <code key="fp">{"owner" + "123"}</code>,
              "Un propriétaire. Possède les maisons de démonstration et décide qui vient.",
            ],
            [
              <code key="g">guest</code>,
              <code key="gp">{"guest" + "123"}</code>,
              "Un invité classique, avec des réservations existantes.",
            ],
            [
              <code key="c">cousin</code>,
              <code key="cp">{"cousin" + "123"}</code>,
              "Un utilisateur normal — essayez la remise famille avec celui-ci.",
            ],
          ]}
        />
        <Callout tone="warn" label="Avant l'usage réel">
          <P>
            Ces mots de passe sont écrits sur une page publique. Avant que la
            famille utilise le site pour de vrai, changez-les — l'admin peut
            définir un nouveau mot de passe pour n'importe quel compte en
            quelques secondes.
          </P>
        </Callout>
      </>
    ),
  },
  {
    id: "sign-in",
    num: "1.3",
    part: 1,
    title: "Comment se connecter",
    body: (
      <>
        <OL>
          <li>
            Ouvrez le site. En haut à droite, appuyez sur{" "}
            <strong>Connexion</strong>.
          </li>
          <li>
            Tapez le nom d'utilisateur et le mot de passe. Il n'y a rien d'autre à
            remplir.
          </li>
          <li>Appuyez sur le bouton. Vous êtes dedans.</li>
        </OL>
        <P>
          Votre nom apparaît en haut. Appuyez dessus pour changer la langue, le
          thème (couleurs du jour ou de la nuit), ou pour partir (
          <strong>Déconnexion</strong>).
        </P>
      </>
    ),
  },

  // ---- Partie 2 · Le parcours principal ----
  {
    id: "book-a-stay",
    num: "2.1",
    part: 2,
    title: "Réserver une maison (l'essentiel)",
    body: (
      <>
        <OL>
          <li>
            Appuyez sur <strong>Découvrir</strong> en haut et ouvrez une maison
            qui vous plaît.
          </li>
          <li>
            Choisissez vos jours sur le calendrier : première nuit, dernière
            nuit. Vert veut dire libre.
          </li>
          <li>Dites combien de personnes viennent.</li>
          <li>
            Répondez à une seule question : vous voulez{" "}
            <strong>toute la maison</strong> (Privé), ou vous acceptez de{" "}
            <strong>la partager</strong> (Partagé) ? Puis appuyez sur le bouton de
            demande.
          </li>
          <li>
            Attendez un oui. Votre demande apparaît d'abord{" "}
            <strong>en attente</strong> ; quand c'est approuvé, votre nom apparaît
            sur ces jours et vous recevez un message.
          </li>
        </OL>
        <Callout tone="note" label="Deux façons de réserver">
          <P>
            Vous pouvez aussi appuyer sur un jour vert du grand calendrier de
            votre <strong>Tableau de bord</strong> — la même fenêtre de
            réservation s'ouvre.
          </P>
        </Callout>
      </>
    ),
  },
  {
    id: "read-the-calendar",
    num: "2.2",
    part: 2,
    title: "Lire le calendrier",
    body: (
      <>
        <P>
          Le calendrier utilise cinq couleurs. C'est tout ce qu'il faut retenir :
        </P>
        <DocTable
          head={["Couleur", "Signification"]}
          rows={[
            [
              <span key="g" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-emerald-500" />
                Vert
              </span>,
              "Libre. Vous pouvez demander ce jour.",
            ],
            [
              <span key="r" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-rose-500" />
                Rouge
              </span>,
              "Toute la maison est prise (réservation privée).",
            ],
            [
              <span key="b" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-sky-400" />
                Bleu
              </span>,
              "Séjour partagé — plusieurs personnes dans la maison.",
            ],
            [
              <span key="o" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm border-2 border-dashed border-amber-500" />
                Orange
              </span>,
              "Demandé, pas encore approuvé.",
            ],
            [
              <span key="y" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-zinc-300" />
                Gris
              </span>,
              "La maison est fermée (choix du propriétaire).",
            ],
          ]}
        />
        <P>
          Appuyez sur n'importe quel jour pour voir qui s'y trouve. Les
          propriétaires et le personnel de la maison trouveront aussi les
          boutons <strong>Approuver</strong> et <strong>Rejeter</strong> pour les
          demandes en attente.
        </P>
      </>
    ),
  },
  {
    id: "my-trips",
    num: "2.3",
    part: 2,
    title: "Vos voyages & l'annulation",
    body: (
      <>
        <P>
          Tout ce que vous avez demandé se trouve sous <strong>Mes voyages</strong>{" "}
          en haut : ce qui arrive, ce qui est passé, et le statut de chaque
          demande.
        </P>
        <L>
          <li>
            Une demande encore <strong>en attente</strong> peut être annulée par
            vous à tout moment.
          </li>
          <li>
            Un séjour <strong>approuvé</strong> est annulé par le propriétaire ou
            un admin — demandez-leur simplement.
          </li>
        </L>
      </>
    ),
  },

  // ---- Partie 3 · Propriétaires, aides & mots ----
  {
    id: "who-does-what",
    num: "3.1",
    part: 3,
    title: "Qui fait quoi",
    body: (
      <>
        <P>
          Chaque compte a un badge. Le badge décide ce que la personne voit et
          peut faire :
        </P>
        <DocTable
          head={["Badge", "Peut faire"]}
          rows={[
            [
              <DocBadge key="v" tone="neutral">
                Visiteur
              </DocBadge>,
              "Seulement regarder les maisons.",
            ],
            [
              <DocBadge key="u" tone="neutral">
                Utilisateur
              </DocBadge>,
              "Réserver des maisons, voir ses voyages, payer les factures qui lui sont assignées.",
            ],
            [
              <DocBadge key="o" tone="violet">
                OWNER
              </DocBadge>,
              "Possède une ou plusieurs maisons : contrôle total sur elles, valide les demandes, voit leurs factures.",
            ],
            [
              <DocBadge key="m" tone="sky">
                MODERATOR
              </DocBadge>,
              "Un aide. Les admins peuvent assigner des modérateurs à des maisons précises — ils valident alors les réservations et gèrent les factures de ces maisons uniquement.",
            ],
            [
              <DocBadge key="a" tone="dark">
                ADMIN
              </DocBadge>,
              "Gère le site : utilisateurs, rôles, maisons, réglages, tout.",
            ],
          ]}
        />
        <P>
          Un admin ou un propriétaire peut créer un compte en moins d'une minute
          — un nom d'utilisateur suffit, tout le reste est facultatif, et le site
          peut même inventer le mot de passe.
        </P>
      </>
    ),
  },
  {
    id: "manage-a-house",
    num: "3.2",
    part: 3,
    title: "Gérer une maison",
    body: (
      <>
        <P>
          Ouvrez une page de maison en tant que propriétaire ou admin : une{" "}
          <strong>barre de gestion</strong> apparaît en haut. Tout se modifie
          directement sur la page :
        </P>
        <DocTable
          head={["Bouton", "Ce qu'il fait"]}
          rows={[
            ["Modifier", "Changer le titre, la description, le prix, la remise famille et le groupe familial."],
            ["Disponibilité", "Choisir les jours où la maison peut être réservée."],
            ["Photos", "Ajouter des photos — ou cliquer sur n'importe quelle image de la page pour la remplacer directement."],
            ["Prix de saison", "Définir un prix plus élevé pour les dates de haute saison."],
            ["Masquer / Publier", "Une maison masquée disparaît de la liste publique jusqu'à sa republication."],
            ["Supprimer", "Retire la maison et ses réservations (avec un avertissement d'abord)."],
          ]}
        />
        <P>
          Les demandes en attente arrivent aussi vers vous : elles apparaissent
          sur le calendrier de votre <strong>Tableau de bord</strong> et sur la
          page de la maison — ouvrez le jour et appuyez sur{" "}
          <strong>Approuver</strong> ou <strong>Rejeter</strong>.
        </P>
      </>
    ),
  },
  {
    id: "house-staff",
    num: "3.3",
    part: 3,
    title: "Le personnel & les familles",
    body: (
      <>
        <P>
          Une maison peut avoir sa propre équipe. Sur la page de la maison,
          appuyez sur <strong>Personnel de la maison</strong> et ajoutez un
          modérateur : à partir de là, il peut valider les réservations et gérer
          les factures de <strong>cette maison uniquement</strong> — il ne voit
          rien d'autre.
        </P>
        <P>
          Les maisons appartiennent aussi à des <strong>groupes
          familiaux</strong>. Les membres de la famille d'une maison paient
          automatiquement le prix réduit chez elle — vous choisissez le
          pourcentage en modifiant la maison.
        </P>
        <Callout tone="note" label="Bon à savoir">
          <P>
            L'écran Permissions (admin uniquement) permet d'ajuster finement ce
            que chaque rôle peut faire. Les valeurs par défaut sont sensibles —
            vous n'en aurez besoin que plus tard.
          </P>
        </Callout>
      </>
    ),
  },
  {
    id: "bills-and-money",
    num: "3.4",
    part: 3,
    title: "Factures et argent",
    body: (
      <>
        <P>
          Les séjours et les travaux dans les maisons créent des{" "}
          <strong>factures</strong> : chacune a un numéro, un montant, et une
          personne qui doit la payer.
        </P>
        <L>
          <li>
            La page <strong>Dépenses</strong> a un sélecteur de maison en haut —
            choisissez une maison pour voir seulement ses factures.
          </li>
          <li>
            Chaque facture en attente montre une{" "}
            <strong>étiquette d'assignation</strong>. Appuyez dessus et choisissez
            le membre de la famille qui doit payer — vous pouvez le changer à tout
            moment.
          </li>
          <li>
            Quand l'argent arrive, appuyez sur <strong>Marquer payée</strong> et
            choisissez qui a payé.
          </li>
          <li>
            La page montre aussi les <strong>dépenses par mois</strong> et{" "}
            <strong>où va l'argent</strong> par catégorie.
          </li>
        </L>
      </>
    ),
  },
  {
    id: "small-dictionary",
    num: "3.5",
    part: 3,
    title: "Petit dictionnaire",
    body: (
      <>
        <P>
          Les mots que vous rencontrerez sur le site — traduits de l'informatique
          en français normal :
        </P>
        <DocTable
          head={["Mot sur le site", "Ce que ça veut dire"]}
          rows={[
            ["Connexion / Déconnexion", "Entrer dans le site / le quitter."],
            ["Découvrir", "Regarder les maisons."],
            ["Tableau de bord", "Votre page de résumé personnelle."],
            ["Mes voyages", "Vos demandes et vos séjours."],
            ["En attente", "Envoyé, en attente d'un oui."],
            ["Approuvé / Rejeté", "Le propriétaire ou l'équipe a dit oui / non."],
            ["Privé", "Vous avez toute la maison."],
            ["Partagé", "Vous partagez la maison avec d'autres."],
            ["Disponibilité", "Les jours où une maison peut être réservée."],
            ["Masquer / Publier", "Retirer une maison de / la remettre sur la liste publique."],
            ["Personnel de la maison", "Des modérateurs assignés à une maison."],
            ["Facture", "Une note : quoi, combien, qui paie."],
            ["Assignation", "La personne qui doit payer une facture."],
            ["Annuler gentiment", "Effacer une facture (personne ne la paie)."],
          ]}
        />
      </>
    ),
  },
  {
    id: "small-help",
    num: "3.6",
    part: 3,
    title: "Petit secours",
    body: (
      <>
        <L>
          <li>
            <strong>Mot de passe oublié ?</strong> Demandez à un admin (le compte{" "}
            <code>admin</code>, ou la personne qui gère le site) — il peut vous en
            donner un nouveau en quelques secondes.
          </li>
          <li>
            <strong>Un jour refuse de se choisir ?</strong> Il est pris, ou la
            maison est fermée. Essayez d'autres jours.
          </li>
          <li>
            <strong>Une maison manque dans votre liste de Dépenses ?</strong>{" "}
            Utilisez le sélecteur de maison en haut et choisissez{" "}
            <em>Toutes les maisons</em>.
          </li>
          <li>
            <strong>Mauvaise langue ?</strong> Appuyez sur le drapeau en haut et
            choisissez la vôtre.
          </li>
          <li>
            <strong>Quelque chose a l'air cassé ?</strong> Quittez la page et
            revenez — ça répare la plupart des petits soucis. Sinon, prévenez la
            personne qui gère le site.
          </li>
        </L>
      </>
    ),
  },
];
